<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useDateTime } from '@ai/ui-components'
import { useLocalStorage } from '@vueuse/core'
import type { DocumentItem } from 'shared/src'
import { formatFileSize } from '@/utils/number.ts'
import { FILE_TYPE_META } from '@/constants/documents.ts'
import { useDocumentFilters } from '@/composables/useDocumentFilters.ts'
import { useQueryClient } from '@tanstack/vue-query'
import { useDocumentsListQuery } from '@/composables/queries/documents'
import { LIST_ACTION_ICONS, ActionButton } from '@/components/BusinessLogic/TableActions'
import { useDocumentDownload } from '@/composables/knowledge/useDocumentDownload.ts'
import { useDocumentDelete } from '@/composables/knowledge/useDocumentDelete.ts'
import { useToast } from '@ai/ui-components/toast'
import { useKnowledgeApi } from '@/composables/api/useKnowledgeApi.ts'
import { useFeatureFlags } from '@/composables/env/useFeatureFlags.ts'
import { STORAGE_KEYS } from '@/enums/StorageEnum'

const toast = useToast()
const { getDocumentPreview, getDocumentPreviewUrl } = useKnowledgeApi()
const { isLocalDev } = useFeatureFlags()
const { t } = useI18n()
const { formatDateTime } = useDateTime()

const queryClient = useQueryClient()

// Load preferences synchronously at setup time (before creating queryParams)
// so the initial TanStack Query uses stored filter values.
const { searchTerm, selectedTypes, initialLoadComplete, loadPreferences } = useDocumentFilters({
  documentTypes: computed(() => []),
})

const preferences = loadPreferences()
selectedTypes.value = preferences.types

const currentPage = ref(1)
const itemsPerPage = useLocalStorage<number>(STORAGE_KEYS.KNOWLEDGE_PAGE_SIZE, 20)

const queryParams = computed(() => ({
  q: searchTerm.value,
  types: selectedTypes.value,
  page: currentPage.value,
  pageSize: itemsPerPage.value,
}))

const { data, isLoading } = useDocumentsListQuery(queryParams)

const documents = computed(() => data.value?.documents ?? [])
const documentTypes = computed(() => data.value?.types ?? [])

const paginationBinding = computed(() => ({
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  currentPage: data.value?.pagination?.page ?? 1,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  itemsPerPage: data.value?.pagination?.pageSize ?? itemsPerPage.value,
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  totalItems: data.value?.pagination?.total ?? 0,
}))

const currentPageModel = computed({
  get: () => paginationBinding.value.currentPage,
  set: (v: number) => {
    currentPage.value = v
  },
})

const itemsPerPageModel = computed({
  get: () => paginationBinding.value.itemsPerPage,
  set: (v: number) => {
    itemsPerPage.value = v
    currentPage.value = 1
  },
})

// Reset page on filter change
watch([searchTerm, selectedTypes], () => {
  currentPage.value = 1
})

// Set initialLoadComplete when first data arrives
watch(data, (value) => {
  if (value && !initialLoadComplete.value) {
    initialLoadComplete.value = true
  }
})

const failedPreviewIds = ref<Set<string | number>>(new Set())

const isLiveDocument = (item: DocumentItem) => item.status === 'success'
const NON_PREVIEWABLE_TYPES = [
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]
const canPreviewDocument = (item: DocumentItem) =>
  isLiveDocument(item) &&
  !NON_PREVIEWABLE_TYPES.includes(item.fileType) &&
  !failedPreviewIds.value.has(item.id)
const previewTooltipContent = (item: DocumentItem) => {
  if (item.status === 'pending') return t('knowledge.preview.unavailable_while_processing')
  if (item.status === 'failed') return t('knowledge.preview.unavailable')
  if (NON_PREVIEWABLE_TYPES.includes(item.fileType)) return t('knowledge.preview.unavailable')
  if (failedPreviewIds.value.has(item.id)) return t('knowledge.preview.unavailable_for_file')
  return t('preview_document')
}

const selectedKeys = ref<(string | number)[]>([])
const openImportModal = ref(false)
const openPreviewModal = ref(false)
const previewBlob = ref<Blob | null>(null)
const previewLoading = ref(false)
const selectedForPreview = ref<DocumentItem>()
const document = ref<DocumentItem>()

const PREVIEW_UNAVAILABLE_KEY = 'knowledge.preview.unavailable_for_file'
const HTML_PREVIEW_UNAVAILABLE_KEY = 'knowledge.preview.invalid_html'
const HTML_MIME_TYPE = 'text/html'
const HTML_RAW_TEXT_ELEMENTS = new Set(['style', 'textarea', 'title'])
const UNSUPPORTED_HTML_TAGS = new Set([
  'script',
  'iframe',
  'object',
  'embed',
  'applet',
  'frame',
  'frameset',
  'base',
  'link',
  'meta',
])
const HTML_VOID_ELEMENTS = new Set([
  'area',
  'base',
  'br',
  'col',
  'embed',
  'hr',
  'img',
  'input',
  'link',
  'meta',
  'param',
  'source',
  'track',
  'wbr',
])

// Keep current-page and selected off-page documents so bulk download
// can still resolve statuses after pagination.
const knownDocumentsById = reactive<Record<string, DocumentItem>>({})

const downloadableSelectedIds = computed(() => {
  return selectedKeys.value.filter((id) => {
    const doc = knownDocumentsById[String(id)]
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    return doc != null && doc.status !== 'failed' && doc.status !== 'pending'
  })
})

const { isBulkDownloading, downloadDocument, onBulkDownload, downloadAllActions } =
  useDocumentDownload(downloadableSelectedIds, selectedKeys)

const {
  openDeleteModal,
  showBulkDeleteLoader,
  deleteModalItem,
  toggleDeleteModal,
  openBulkDeleteModal,
  onConfirmDelete,
} = useDocumentDelete(selectedKeys)

const handleDeleteConfirm = async () => {
  await onConfirmDelete()
  openPreviewModal.value = false
  return true
}

const STATUS_COLORS = {
  success: 'success',
  failed: 'error',
  pending: 'neutral',
} as const

const getStatusMeta = (status?: string) => {
  if (!status) return null
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const color = STATUS_COLORS[status as keyof typeof STATUS_COLORS] || 'neutral'
  return { label: t(`knowledge.status.${status}`), color }
}

const listColumns = computed(() => [
  { key: 'fileName', label: t('file_name') },
  { key: 'fileType', label: t('file_type') },
  { key: 'status', label: t('status') },
  { key: 'created_at', label: t('created_at') },
  { key: 'last_modification', label: t('last_updated') },
  { key: 'created_by', label: t('created_by') },
  { key: 'size', label: t('size') },
])

const toggleModal = (item: DocumentItem) => {
  document.value = item
  openImportModal.value = !openImportModal.value
}

const findHtmlTagEnd = (html: string, start: number) => {
  let quote: string | null = null

  for (let index = start + 1; index < html.length; index += 1) {
    const char = html[index]

    if (quote) {
      if (char === quote) quote = null
      continue
    }

    if (char === '"' || char === "'") {
      quote = char
      continue
    }

    if (char === '>') return index
  }

  return -1
}

const isWellFormedHtml = (html: string) => {
  const stack: string[] = []
  let index = 0

  while (index < html.length) {
    const tagStart = html.indexOf('<', index)
    if (tagStart === -1) return stack.length === 0

    if (html.startsWith('<!--', tagStart)) {
      const commentEnd = html.indexOf('-->', tagStart + 4)
      if (commentEnd === -1) return false
      index = commentEnd + 3
      continue
    }

    const nextChar = html[tagStart + 1]
    if (nextChar === '!' || nextChar === '?') {
      const tagEnd = findHtmlTagEnd(html, tagStart)
      if (tagEnd === -1) return false
      index = tagEnd + 1
      continue
    }

    let tagNameStart = tagStart + 1
    while (/\s/.test(html[tagNameStart] ?? '')) tagNameStart += 1

    const isClosingTag = html[tagNameStart] === '/'
    if (isClosingTag) {
      tagNameStart += 1
      while (/\s/.test(html[tagNameStart] ?? '')) tagNameStart += 1
    }

    const tagNameMatch = /^[a-zA-Z][\w:-]*/.exec(html.slice(tagNameStart))
    if (!tagNameMatch) {
      index = tagStart + 1
      continue
    }

    const tagName = tagNameMatch[0].toLowerCase()
    if (UNSUPPORTED_HTML_TAGS.has(tagName)) return false

    const tagEnd = findHtmlTagEnd(html, tagStart)
    if (tagEnd === -1) return false

    if (isClosingTag) {
      if (stack.pop() !== tagName) return false
      index = tagEnd + 1
    } else if (HTML_VOID_ELEMENTS.has(tagName) || html[tagEnd - 1] === '/') {
      index = tagEnd + 1
    } else if (HTML_RAW_TEXT_ELEMENTS.has(tagName)) {
      const closingTagStart = html.toLowerCase().indexOf(`</${tagName}`, tagEnd + 1)
      if (closingTagStart === -1) return false
      stack.push(tagName)
      index = closingTagStart
    } else {
      stack.push(tagName)
      index = tagEnd + 1
    }
  }

  return stack.length === 0
}

const readBlobAsText = async (blob: Blob) => {
  if (typeof blob.text === 'function') {
    return blob.text()
  }
  if (typeof FileReader !== 'undefined') {
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        resolve((reader.result as string) || '')
      }
      reader.onerror = () => {
        reject(reader.error ?? new Error('FileReader error'))
      }
      reader.readAsText(blob)
    })
  }
  return new Response(blob).text()
}

const isValidPreviewBlob = async (item: DocumentItem, blob: Blob) => {
  if (!item.fileType.toLowerCase().startsWith(HTML_MIME_TYPE)) return true
  const html = await readBlobAsText(blob)
  return isWellFormedHtml(html)
}

const disablePreviewForFile = (item: DocumentItem, messageKey = PREVIEW_UNAVAILABLE_KEY) => {
  failedPreviewIds.value.add(item.id)
  if (selectedForPreview.value?.id === item.id) {
    selectedForPreview.value = undefined
  }
  toast.error(t(messageKey), { title: t('error_occurred') })
}

const openPreview = async (item: DocumentItem) => {
  if (!canPreviewDocument(item)) return
  selectedForPreview.value = item
  previewLoading.value = true
  try {
    let blob: Blob | null = null
    if (isLocalDev) {
      const url = await getDocumentPreviewUrl(item.id)
      if (url) {
        const response = await fetch(url)
        if (response.ok) {
          blob = await response.blob()
        }
      }
    } else {
      blob = await getDocumentPreview(item.id)
    }
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (selectedForPreview.value?.id !== item.id) return
    if (!blob) {
      disablePreviewForFile(item)
      return
    }
    if (!(await isValidPreviewBlob(item, blob))) {
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (selectedForPreview.value?.id !== item.id) return
      disablePreviewForFile(item, HTML_PREVIEW_UNAVAILABLE_KEY)
      return
    }
    previewBlob.value = blob
    openPreviewModal.value = true
  } catch {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (selectedForPreview.value?.id !== item.id) return
    disablePreviewForFile(item)
  } finally {
    previewLoading.value = false
  }
}

const getActions = (
  item: DocumentItem,
  opts?: {
    onPreview?: () => void
    previewDisabled?: boolean
    onImport?: () => void
  },
) => {
  const actions = []

  actions.push({
    label: t('preview_document'),
    value: 'preview',
    icon: LIST_ACTION_ICONS.preview,
    handler: () => opts?.onPreview?.(),
    danger: false,
    disabled: opts?.previewDisabled ?? false,
    dataTestid: 'action-preview',
  })

  actions.push({
    label: t('update'),
    value: 'import',
    icon: LIST_ACTION_ICONS.import,
    handler: () => opts?.onImport?.(),
    danger: false,
    dataTestid: 'action-import',
  })

  actions.push({
    label: t('download'),
    value: 'download',
    icon: LIST_ACTION_ICONS.download,
    handler: () => downloadDocument(item),
    danger: false,
    disabled: item.status === 'failed' || item.status === 'pending',
    dataTestid: 'action-download',
  })

  actions.push({
    label: t('delete'),
    value: 'delete',
    icon: LIST_ACTION_ICONS.deleteDanger,
    handler: () => {
      toggleDeleteModal(item)
    },
    danger: true,
    dataTestid: 'action-delete',
  })

  return actions
}

const onImportSuccess = async () => {
  await queryClient.invalidateQueries({ queryKey: ['documents', 'list'] })
}

const getBulkSelectionSummary = (selectedCount: number) =>
  selectedCount === 1 ? t('one_document_selected') : t('n_documents_selected', [selectedCount])

watch(
  [selectedKeys, documents],
  ([keys, items]: [(string | number)[], DocumentItem[]]) => {
    for (const item of items) {
      knownDocumentsById[item.id] = item
    }

    const keysToKeep = new Set([...keys.map((k) => k), ...items.map((item) => item.id)])

    for (const id of Object.keys(knownDocumentsById)) {
      if (!keysToKeep.has(id)) {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete knownDocumentsById[id]
      }
    }
  },
  { immediate: true },
)

watch(openPreviewModal, (isOpen) => {
  if (!isOpen) {
    selectedForPreview.value = undefined
    previewBlob.value = null
  }
})
</script>
<template>
  <OTable
    id="documents"
    :columns="listColumns"
    :modelValue="documents"
    v-model:selectedKeys="selectedKeys"
    with-pagination
    :pagination="paginationBinding"
    v-model:currentPage="currentPageModel"
    v-model:itemsPerPage="itemsPerPageModel"
    sortable
    resizable
    :loading="isLoading || showBulkDeleteLoader"
    default-sort-key="last_modification"
    default-sort-order="desc"
    with-bulk-actions
    row-key="id"
    column-resize-mode="indicator"
  >
    <template #fileType="{ item }">
      <div class="flex items-center space-x-2">
        <UiIcon
          color="text-neutral-1"
          :name="FILE_TYPE_META[item.fileType]?.icon"
          variant="outline"
          :clickable="false"
        />

        <OOverflowTooltip :content="FILE_TYPE_META[item.fileType]?.label ?? ''">
          <span class="select-none">{{ FILE_TYPE_META[item.fileType]?.label }}</span>
        </OOverflowTooltip>
      </div>
    </template>

    <template #status="{ item }">
      <OBadge
        v-if="getStatusMeta(item.status)"
        variant="primary"
        :color="getStatusMeta(item.status)!.color"
        :content="getStatusMeta(item.status)!.label"
      />
      <span v-else>-</span>
    </template>

    <template #created_at="{ item }">
      <OOverflowTooltip
        :content="formatDateTime({ format: 'dateTime', value: item.created_at, fallback: '-' })"
      >
        {{ formatDateTime({ format: 'dateTime', value: item.created_at, fallback: '-' }) }}
      </OOverflowTooltip>
    </template>

    <template #last_modification="{ item }">
      <OOverflowTooltip
        :content="
          formatDateTime({ format: 'dateTime', value: item.last_modification, fallback: '-' })
        "
      >
        {{ formatDateTime({ format: 'dateTime', value: item.last_modification, fallback: '-' }) }}
      </OOverflowTooltip>
    </template>

    <template #created_by="{ item }">
      <OOverflowTooltip :content="item.created_by || '-'">
        {{ item.created_by || '-' }}
      </OOverflowTooltip>
    </template>

    <template #size="{ item }">
      <OOverflowTooltip :content="formatFileSize(item.size)">
        {{ formatFileSize(item.size) }}
      </OOverflowTooltip>
    </template>

    <template #filterActions>
      <div class="flex items-center justify-between">
        <div class="flex items-center space-x-3">
          <OInput
            v-model="searchTerm"
            iconSearch
            :placeholder="t('search')"
            size="S"
            dataTestid="configurators-search"
            class="w-[264px]"
          />
          <ODropdown
            v-model="selectedTypes"
            :options="documentTypes"
            medium
            :searchable="documentTypes.length >= 6"
            multi-select
            :filter-title="
              selectedTypes.length === documentTypes.length ? t('all_file_types') : t('file_types')
            "
            filter
            class="!w-fit"
            dataTestid="configurators-languages"
          />
          <!-- TODO: turn back once the BE owner is supported -->
          <!-- <ODropdown
            v-model="selectedOwners"
            :options="formattedOwners"
            medium
            :searchable="formattedOwners.length >= 6"
            multi-select
            :filter-title="allOwnersSelected ? t('all_owners') : t('owners')"
            filter
            class="!w-fit"
            dataTestid="configurators-languages"
          /> -->
        </div>

        <div class="flex justify-end items-center w-full gap-2">
          <OListActions
            v-if="false"
            small
            data-testid="documents-page-actions"
            :actions="downloadAllActions"
          />
          <DocumentsImportModal
            v-model:open="openImportModal"
            :document-item="document"
            @success="onImportSuccess"
          >
            <template #default="{ handleClick }">
              <OButton
                size="M"
                :label="t('import')"
                variant="secondary"
                @click="
                  () => {
                    document = undefined
                    handleClick()
                  }
                "
                dataTestid="documents-btn-import"
              />
            </template>
          </DocumentsImportModal>
        </div>
      </div>
    </template>

    <template #bulk-selection-summary="{ selectedCount }">
      <span
        class="typography-body-2-strong text-neutral-1"
        data-testid="documents-bulk-selection-summary"
      >
        {{ getBulkSelectionSummary(selectedCount) }}
      </span>
    </template>

    <template #bulk-actions="{ selectedCount }">
      <div class="flex items-center">
        <OButton
          v-if="downloadableSelectedIds.length > 0"
          :label="t('download')"
          variant="tertiary"
          size="S"
          :disabled="isBulkDownloading"
          :loading="isBulkDownloading"
          dataTestid="documents-bulk-download"
          @click="onBulkDownload"
        />
        <OButton
          :label="t('delete')"
          variant="tertiary"
          color="error"
          size="S"
          :disabled="selectedCount === 0"
          dataTestid="documents-bulk-delete"
          @click="openBulkDeleteModal"
        />
      </div>
    </template>

    <template #actions="{ item }">
      <ActionButton
        :tooltip="previewTooltipContent(item)"
        icon="preview"
        :disabled="!canPreviewDocument(item)"
        :loading="previewLoading && selectedForPreview?.id === item.id"
        @click="openPreview(item)"
        data-testid="row-action-preview"
      />

      <ActionButton
        :tooltip="t('update')"
        icon="import"
        @click="toggleModal(item)"
        data-testid="row-action-import"
      />

      <OListActions
        data-testid="row-action-more"
        small
        :actions="
          getActions(item as DocumentItem, {
            onPreview: () => openPreview(item as DocumentItem),
            previewDisabled: !canPreviewDocument(item),
            onImport: () => toggleModal(item),
          })
        "
      />
    </template>
  </OTable>

  <DocumentPreviewModal
    v-if="selectedForPreview"
    v-model:open="openPreviewModal"
    :id="selectedForPreview.id"
    :type="selectedForPreview.fileType"
    :document-name="selectedForPreview.fileName"
    :size="selectedForPreview.size"
    :created-at="selectedForPreview.created_at"
    :last-updated="selectedForPreview.last_modification"
    :owner="selectedForPreview.created_by"
    :blob="previewBlob"
    @delete="() => toggleDeleteModal(selectedForPreview!)"
    @import="
      () => {
        openPreviewModal = false
        toggleModal(selectedForPreview!)
      }
    "
    @download="() => selectedForPreview && downloadDocument(selectedForPreview)"
  />

  <DeleteDocumentModal
    v-if="openDeleteModal"
    v-model:open="openDeleteModal"
    :item="deleteModalItem"
    :secondary-action-promise="handleDeleteConfirm"
    data-testid="row-delete-modal"
  />
</template>

<style scoped>
:deep([data-testid='otable-bulk-actions-row']) {
  background-color: var(--color-grayscale-100) !important;
  border-radius: var(--size-radius-m) !important;
}

:deep([data-testid='otable-bulk-actions-spacer']) {
  padding-top: calc(var(--spacing) * 4);
}
</style>
